import {
  ApiOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  DatabaseOutlined,
  LinkOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Spin, Tag, Typography } from "antd";
import moment from "moment";
import type React from "react";
import { useCallback } from "react";
import { color, font, iconTile } from "theme/aiInventory";

import { EmptyState } from "./EmptyState.tsx";
import type { ManagedMcpServer } from "./hooks/useManagedMcpServers.ts";
import type { McpClient } from "./hooks/useMcpClients.ts";

const rowBaseStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 16px",
  borderRadius: 6,
  background: color.card,
  display: "grid",
  gridTemplateRows: "auto auto",
  alignItems: "center",
  columnGap: 16,
  rowGap: 2,
  minHeight: 72,
  textAlign: "left",
  fontFamily: font.sans,
};

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const cellRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
};

const monoTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: color.inkMute,
  fontFamily: font.mono,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const titleStyle: React.CSSProperties = {
  fontWeight: 500,
  fontSize: 14,
  color: color.ink,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const subtitleStyle: React.CSSProperties = {
  gridColumn: "2 / 6",
  fontSize: 11,
  color: color.inkMute,
  fontFamily: font.mono,
  letterSpacing: "0.01em",
  whiteSpace: "normal",
  marginTop: 2,
};

const iconCircleStyle: React.CSSProperties = {
  gridRow: "1 / 3",
  width: 36,
  height: 36,
  borderRadius: "50%",
  background: color.mute,
  color: color.controlPlane,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  fontSize: 18,
};

const stateColor = (state: string): string =>
  state === "installed" ? "green" : "default";

const endpointFor = (server: ManagedMcpServer): string =>
  server.upstream ?? server.url ?? server.image ?? "-";

const formatTimestamp = (timestamp: number | undefined): string =>
  timestamp ? moment(timestamp).fromNow() : "-";

type LoadingPanelProps = {
  loading: boolean;
  children: React.ReactNode;
};

const LoadingPanel: React.FC<LoadingPanelProps> = ({ loading, children }) =>
  loading ? <Spin /> : children;

export type McpServersListProps = {
  servers: ManagedMcpServer[];
  loading: boolean;
  onSelect?: (server: ManagedMcpServer) => void;
};

type McpServerRowProps = {
  server: ManagedMcpServer;
  onSelect?: (server: ManagedMcpServer) => void;
};

const McpServerRow: React.FC<McpServerRowProps> = ({ server, onSelect }) => {
  const handleClick = useCallback(() => onSelect?.(server), [onSelect, server]);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!onSelect) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(server);
      }
    },
    [onSelect, server]
  );
  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      className="p0-agent-row"
      data-testid={`mcp-server-row-${server.id}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={server.label}
      style={{
        ...rowBaseStyle,
        cursor: onSelect ? "pointer" : "default",
        gridTemplateColumns:
          "40px minmax(220px, 1.6fr) 160px 160px minmax(220px, 1fr)",
      }}
    >
      <div aria-hidden style={iconCircleStyle}>
        <DatabaseOutlined />
      </div>
      <Typography.Text style={titleStyle}>{server.label}</Typography.Text>
      <div style={cellRowStyle}>
        <span style={iconTile("blue")}>
          <ApiOutlined aria-hidden />
        </span>
        <Typography.Text style={monoTextStyle}>{server.type}</Typography.Text>
      </div>
      <div style={cellRowStyle}>
        <Tag color={stateColor(server.state)}>{server.state}</Tag>
      </div>
      <div style={cellRowStyle}>
        <span style={iconTile("neutral")}>
          <LinkOutlined aria-hidden />
        </span>
        <Typography.Text style={monoTextStyle}>
          {endpointFor(server)}
        </Typography.Text>
      </div>
      <Typography.Text style={subtitleStyle}>
        {server.id} | {server.path}
      </Typography.Text>
    </div>
  );
};

/**
 * Renders managed MCP server inventory using the same visual row language as
 * the Agents tab.
 */
export const McpServersList: React.FC<McpServersListProps> = ({
  servers,
  loading,
  onSelect,
}) => (
  <LoadingPanel loading={loading}>
    {servers.length === 0 ? (
      <EmptyState
        title="No MCP servers discovered yet"
        hint="Install MCP server entries to populate this inventory."
      />
    ) : (
      <div style={listStyle}>
        {servers.map((server) => (
          <McpServerRow key={server.id} server={server} onSelect={onSelect} />
        ))}
      </div>
    )}
  </LoadingPanel>
);

export type McpClientsListProps = {
  clients: McpClient[];
  loading: boolean;
  onSelect?: (client: McpClient) => void;
};

type McpClientRowProps = {
  client: McpClient;
  onSelect?: (client: McpClient) => void;
};

const McpClientRow: React.FC<McpClientRowProps> = ({ client, onSelect }) => {
  const handleClick = useCallback(() => onSelect?.(client), [onSelect, client]);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!onSelect) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(client);
      }
    },
    [onSelect, client]
  );
  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      className="p0-agent-row"
      data-testid={`mcp-client-row-${client.id}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={client.id}
      style={{
        ...rowBaseStyle,
        cursor: onSelect ? "pointer" : "default",
        gridTemplateColumns:
          "40px minmax(260px, 1.8fr) 180px 190px minmax(220px, 1fr)",
      }}
    >
      <div aria-hidden style={iconCircleStyle}>
        <CodeOutlined />
      </div>
      <Typography.Text style={titleStyle}>{client.id}</Typography.Text>
      <div style={cellRowStyle}>
        <span style={iconTile("neutral")}>
          <UserOutlined aria-hidden />
        </span>
        <Typography.Text style={monoTextStyle}>
          {client.createdBy?.user ?? "-"}
        </Typography.Text>
      </div>
      <div style={cellRowStyle}>
        <span style={iconTile("blue")}>
          <CodeOutlined aria-hidden />
        </span>
        <Typography.Text style={monoTextStyle}>
          {client.platform}
        </Typography.Text>
      </div>
      <div style={cellRowStyle}>
        <span style={iconTile("green")}>
          <ClockCircleOutlined aria-hidden />
        </span>
        <Typography.Text style={monoTextStyle}>
          {formatTimestamp(client.createdAt)}
        </Typography.Text>
      </div>
      <Typography.Text style={subtitleStyle}>
        {client.version} | {client.redirectUri}
      </Typography.Text>
    </div>
  );
};

/**
 * Renders registered MCP clients while intentionally excluding secret hashes.
 */
export const McpClientsList: React.FC<McpClientsListProps> = ({
  clients,
  loading,
  onSelect,
}) => (
  <LoadingPanel loading={loading}>
    {clients.length === 0 ? (
      <EmptyState
        title="No MCP clients registered yet"
        hint="MCP clients appear here after registration with the P0 gateway."
      />
    ) : (
      <div style={listStyle}>
        {clients.map((client) => (
          <McpClientRow key={client.id} client={client} onSelect={onSelect} />
        ))}
      </div>
    )}
  </LoadingPanel>
);
